import React, {
    useEffect, useMemo, useRef, useState,
} from 'react';

import {
    Col, Row, ToggleButton, ToggleButtonGroup,
} from 'react-bootstrap';
import { Link, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';

import ChangeHistory from './ChangeHistory';
import { singular, articleTypes } from '../aliases';
import ChannelsCheckboxes from '../blocks/channels_checkboxes';
import ConditionalRenderer from '../blocks/ConditionalRenderer';
import {
    CheckboxField,
    ControlPageBlock,
    DateTimeField,
    InputField,
    RatingSelectorField,
    SearchSelectorField,
    TextAreaEditorField,
    TextAreaField,
    ImageLeadField,
    SelectorField,
} from '../blocks/edit_blocks';
import FastImageLoad from '../blocks/fast_image_load';
import Gallery from '../blocks/Gallery';
import PermissionManager from '../blocks/PermissionManager';
import SearchDragList from '../blocks/search_drag_list';
import SeoSettings from '../blocks/seo_settings';
import SocialHint from '../blocks/social_hint';
import Fetcher from '../common/communicate';
import Dispatcher from '../common/dispatcher';
import UploadFileInput from '../components/UploadFileInput/UploadFileInput';
import {
    API_DOMAIN, PROJECT_PREFIX, hosts, VIDEO_TYPES,
} from '../const';
import { useUser } from '../contexts/user';
import { buildArticleURL } from '../tools/build_urls';
import clearCache from '../tools/clearCache';
import {
    formatDateTime, formatDateTimeForDRF, getMoscowTime, Text2Slug,
} from '../tools/converters';
import { getBase64 } from '../tools/files';
import { useDebouncedCallback, usePrevious } from '../tools/hooks';
import SelectSearch from '../tools/search_selector';
import VideoPlayer from '../tools/video_player';

const LANGS = ['ru', 'en'];

const disciplinesList = [
    {
        id: 38, slug: 'discipline-phygital-football', name: 'Футбол', isGof: true,
    },
    {
        id: 37, slug: 'discipline-phygital-basketball', name: 'Баскетбол', isGof: true,
    },
    {
        id: 44, slug: 'discipline-phygital-racing', name: 'Гонки', isGof: true,
    },
    {
        id: 45, slug: 'discipline-phygital-fighting', name: 'Единоборства', isGof: true,
    },
    {
        id: 46, slug: 'discipline-phygital-hockey', name: 'Хоккей', isGof: true,
    },
    {
        id: 39, slug: 'discipline-phygital-beat-saber', name: 'Битсейбер', isGof: true,
    },
    {
        id: 40, slug: 'discipline-phygital-drone-racing', name: 'Дрон рейсинг', isGof: true,
    },
    {
        id: 48, slug: 'discipline-phygital-moba', name: 'Боевая арена', isGof: true,
    },
    {
        id: 50, slug: 'discipline-phygital-battle-royale', name: 'Королевская битва', isGof: true,
    },
    {
        id: 51, slug: 'discipline-phygital-speedrun', name: 'Speedrun', isGof: true,
    },
    {
        id: 52, slug: 'discipline-phygital-cs-go', name: 'CS:GO', isGof: true,
    },
    {
        id: 53, slug: 'discipline-phygital-warface', name: 'Warface', isGof: true,
    },
    {
        id: 54, slug: 'discipline-phygital-standoff-2', name: 'Standoff 2', isGof: true,
    },
    {
        id: 55, slug: 'discipline-phygital-mlbb', name: 'MLBB', isGof: true,
    },
    {
        id: 56, slug: 'discipline-phygital-dota-2', name: 'Dota 2', isGof: true,
    },
    {
        id: 57, slug: 'discipline-phygital-tanks', name: 'Мир танков', isGof: true,
    },
    {
        id: 58, slug: 'discipline-phygital-robots', name: 'Роботы', isGof: true,
    },
    {
        id: 59, slug: 'discipline-phygital-programming', name: 'Спортивное программирование', isGof: true,
    },
];

const disciplinesIdList = disciplinesList.map((discipline) => discipline.id);
const gofProject = ['gof.vkplay.ru'];
const ratingAllowedTypes = ['review'];
const disciplinesAllowedTypes = ['gof'];
const viewsDisallowedProjects = ['gof.vkplay.ru'];
const coversDisallowedTypes = ['news', 'video', 'static', 'compilation'];
const galleryDisallowedTypes = ['compilation'];
const textDisallowedTypes = ['compilation'];
const checkboxesDisallowedTypes = ['compilation'];
const fileAllowedTypes = ['file'];

const leadPicVertSize = '375x401';
const leadPicSquareSize = '372x323';

function StoryEditScreenV2({
    type, storyId, isUgc, history, project,
}) {
    const currentDate = useMemo(() => new Date(getMoscowTime()).toISOString(), []);

    const { user, permissions } = useUser();
    const { email, full_name } = user;

    const [currentLang, setLang] = useState('ru');
    const [data, setData] = useState({
        descr: '',
        gallery: '',
        // date_added: currentDate,
        slug: '',
        content: {
            ru: {},
        },
    });

    const [validation, setValidation] = useState({});

    const prevData = usePrevious(data);

    const isVKP = useMemo(() => project.includes('vkplay.ru'), [project]);

    const isArticle = useMemo(() => articleTypes.includes(type), [type]);

    const filteredDisciplines = useMemo(() => {
        if (!data?.channels) {
            return [];
        }

        return data.channels.filter((channel) => disciplinesIdList.includes(channel.id));
    }, [data.channels]);

    const APIendpoint = useMemo(() => (storyId
        ? `v2/${project}/story/${type}/${storyId}/`
        : `v2/${project}/story/`), [project, storyId, type]);

    const leadPicSize = useMemo(() => (type === 'secret' ? '340x200' : '1280x720'), [type]);

    const builtUrl = useMemo(() => {
        if (!project) {
            return '';
        }

        if (isArticle) {
            const url = buildArticleURL(type, data.slug, true);

            return isVKP ? `https://media.vkplay.ru${url.replace('pc/', '')}` : `${window.location.origin}${url}`;
        }

        if (window.location.origin.includes('games.mail.ru')) {
            return `${hosts[project].prod}/${type}/${data?.created_at?.split(' ')[0]}/${data.slug}/`;
        }

        return `${hosts[project].dev}/${type}/${data?.created_at?.split(' ')[0]}/${data.slug}/`;
    }, [project, isArticle, type, data.created_at, data.slug, isVKP]);

    const moderComments = useMemo(() => {
        if (!isUgc) {
            return null;
        }

        return (
            <Row>
                <Col md={8}>
                    <TextAreaField
                        name="moder_comment"
                        label="Комментарий модератора"
                        value={data.moder_comment}
                        onChange={handleChange}
                        rows={2}
                    />
                </Col>
            </Row>
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.moder_comment, isUgc]);

    const videoUrls = useMemo(() => {
        if (!data?.video_urls) {
            return {};
        }

        return data.video_urls.reduce((acc, current) => ({
            ...acc,
            [current.type]: current.url,
        }), {});
    }, [data.video_urls]);

    const socialHintRef = useRef(null);
    const transitionBlocker = useRef(null);

    const initAutoSave = useDebouncedCallback(() => {
        handleSave({ target: { id: 'autosave' }, currentTarget: { id: 'autosave' } });
    }, 10000);

    useEffect(() => {
        if (storyId) {
            Fetcher(APIendpoint, 'GET', handleDataLoad);

            return;
        }

        setData({
            title: '',
            seo_title: '',
            seo_descr: '',
            slug: '',
            is_published: false,
            published_at: formatDateTimeForDRF(currentDate),
            modified_at: currentDate,
            views: 0,
            rating: 0,
            descr: '',
            text: '',
            author_info: {
                email: email || 'games.gamemailru@mail.ru',
                nick: email ? `${full_name}` : 'Игры@Mail.Ru',
            },
            disable_comments: false,
            is_list_hidden: false,
            is_draft: false,
            is_ugc: false,
            in_progress: false,
            games: [],
            channels: [
                ...(project === 'media.vkplay.ru' && type === 'news' ? [{ id: 36, label: 'VK Play Игры', slug: 'vkplay' }] : []),
            ],
            native_pic: '',
            picture: '',
            picture_square: null,
            picture_vertical: null,
            type: type === 'videos' ? 'trailer' : type,
            content: {
                ru: {
                    lang: 'ru',
                    author_email: email || 'games.gamemailru@mail.ru',
                    author_info: {
                        email: email || 'games.gamemailru@mail.ru',
                        nick: email ? `${full_name}` : 'Игры@Mail.Ru',
                    },
                    published_at: formatDateTimeForDRF(currentDate),
                },
                en: {
                    lang: 'en',
                    author_email: email || 'games.gamemailru@mail.ru',
                    author_info: {
                        email: email || 'games.gamemailru@mail.ru',
                        nick: email ? `${full_name}` : 'Игры@Mail.Ru',
                    },
                    published_at: formatDateTimeForDRF(currentDate),
                },
            },
            project_name: project,
            media: [],
            shouldSave: false,
        });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [APIendpoint, currentDate, full_name, storyId, type, email]);

    useEffect(() => {
        if (!prevData?.slug) {
            return;
        }

        const { modified_at, published_at, ...noDateData } = data;
        const { modified_at: prevDate, published_at: prevPublished, ...noDatePrevData } = prevData;

        if (JSON.stringify(noDateData) === JSON.stringify(noDatePrevData)) {
            return;
        }

        initHistoryBlock(true);

        // if (type !== 'news') {
        //     initAutoSave();
        // }

        return () => {
            initHistoryBlock(false);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (!socialHintRef.current) {
            return;
        }

        socialHintRef.current.getTranslations();
    }, [socialHintRef]);

    function handleDataLoad(loadedData) {
        const reducedContent = {};

        // из массива в объект
        loadedData.content.map((langContent) => reducedContent[langContent.lang] = {
            ...langContent,
            author_info: {
                email: 'автор',
                nick: langContent.author?.name,
            },
        });
        // докидываем пустой объект для языков без контента
        LANGS.map((avaliableLang) => { reducedContent[avaliableLang] = reducedContent[avaliableLang] || { lang: avaliableLang }; });

        const newData = {
            ...loadedData,
            content: reducedContent,
            shouldSave: false,
        };

        setData(newData);
    }

    function handleSave(e) {
        if (data.games?.find((game) => !game.id)) {
            toast.error('Код 003. У игры отсутствует ID.');
        }

        validate();

        if (socialHintRef && socialHintRef.current) {
            socialHintRef.current.getTranslations();
        }

        const save_type = e.currentTarget.id;

        const request_method = storyId ? 'PATCH' : 'POST';

        const { content, ...restData } = data;

        const newContent = Object.values(content).filter((langContent) => langContent.title && (type === 'compilation' || langContent.text));

        if (!newContent.length) {
            return;
        }

        const editedData = {
            ...restData,
            content: newContent,
        };

        const callback = (newData) => {
            initHistoryBlock(false);

            if (newData.is_published) {
                clearCache(builtUrl);
            }

            if (save_type === 'save_button') {
                return goToPreviousPage(newData);
            }

            if (!storyId) {
                return goToNewPage(newData);
            }

            handleDataLoad(newData);
        };

        Fetcher(APIendpoint, request_method, callback, editedData);
    }

    function handleContentChange(e) {
        const { id, value } = e.currentTarget;
        const newValue = value;

        const newData = JSON.parse(JSON.stringify(data));

        newData.content[currentLang][id] = newValue;

        setData(newData);
    }

    function handleChange(e) {
        const {
            id, type, value, checked, files, multiData,
        } = e.currentTarget;

        let newValue = value;

        if (type === 'checkbox') {
            newValue = checked;
        }

        if (type === 'file') {
            const file = files[0];
            // let file_size = file.size/1024/1024;
            const file_type = file.type;

            if (file_type !== 'image/jpeg' && file_type !== 'image/png') {
                return;
                // TODO: окно с сообщением, что формат файла не подходит
            }

            getBase64(file, (result) => {
                setData({
                    ...data,
                    [id]: result,
                });
            });

            return;
        }

        if (e.currentTarget.id.includes('video_url')) {
            const url_type = e.currentTarget.id.replace('_video_url', '');

            const newVal = JSON.parse(JSON.stringify(data));

            const urls = newVal.video_urls?.find((x) => x.type === url_type);

            if (urls) {
                urls.url = e.currentTarget.value;
            } else {
                newVal.video_urls = newVal.video_urls || [];
                newVal.video_urls.push({
                    type: url_type,
                    url: e.currentTarget.value,
                });
            }

            setData(newVal);

            return;
        }

        if (e.type === 'socialInsert' && socialHintRef.current) {
            socialHintRef.current.getTranslations();
        }

        const newData = JSON.parse(JSON.stringify(data));

        if (multiData) {
            newData[multiData.id] = [
                ...newData[multiData.id],
                multiData.value,
            ];

            if (!multiData.value.id) {
                toast.error('Код 002. У игры отсутствует ID.');
            }
        }

        if (id === 'disciplines') {
            const otherChannels = newData.channels.filter((channel) => (!disciplinesIdList.includes(channel.id)));
            newData.channels = [
                ...otherChannels,
                ...newValue,
            ];
        } else {
            newData[id] = newValue;
        }

        setData(newData);
    }

    function handleRemoveData(id) {
        setData({
            ...data,
            [id]: '',
        });
    }

    function handleTitleChange(e) {
        const { value } = e.currentTarget;

        const newData = JSON.parse(JSON.stringify(data));

        if (newData.content[currentLang].title === newData.seo_title) {
            newData.seo_title = value;
        }

        newData.content[currentLang].title = value;

        if (!storyId && currentLang === 'ru') {
            newData.slug = Text2Slug(value);
        }

        setData(newData);
    }

    useEffect(() => {
        if (data.shouldSave) {
            handleSave({ target: { id: 'autosave' }, currentTarget: { id: 'autosave' } });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.shouldSave]);

    function handleImagesChange(newImages) {
        setData({
            ...data,
            media: newImages,
            shouldSave: true,
        });
    }

    function handleFileUpload(event) {
        const file = event.currentTarget.files[0];
        const formData = new FormData();

        formData.append('file', file);

        const requestParameters = {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                enctype: 'multipart/form-data',
            },
            body: formData,

        };

        fetch(`${API_DOMAIN}storage/files/`, requestParameters)
            .then((response) => {
                if (!response.ok) {
                    throw response.json();
                }

                if (response.status === 204) {
                    return '';
                }

                return response.json();
            })
            .then((data) => {
                setData((prev) => ({
                    ...prev,
                    media: [
                        ...prev.media.filter(({ type }) => type !== 'file'),
                        {
                            type: 'file',
                            file: data.url,
                            label: data.url.split('/').pop(),
                            size: data.size,
                        },
                    ],
                    shouldSave: true,
                }));

                toast.success('Файл успешно загружен!');
            });
    }

    function handleImageClick(e) {
        const { id } = e.currentTarget;
        const { filename } = e.currentTarget.dataset;

        Dispatcher.emit('PICTURE_ADDED', { id, filename });
    }

    function handleAuthorSearchChange(e) {
        const { value, label } = e.currentTarget.value;

        setData({
            ...data,
            content: {
                ...data.content,
                [currentLang]: {
                    ...data.content[currentLang],
                    author_email: value,
                    author_info: {
                        email: value,
                        nick: label,
                    },
                },
            },
        });
    }

    function handleDateChange(date) {
        setData({
            ...data,
            published_at: date,
            content: {
                ...data.content,
                [currentLang]: {
                    ...data.content[currentLang],
                    published_at: date,
                },
            },
        });
    }

    function handleRemoveStory() {
        Fetcher(APIendpoint, 'DELETE', goToPreviousPage);
    }

    function handleRejectUGC() {
        const clonedData = JSON.parse(JSON.stringify(data));
        clonedData.is_draft = true;
        setData(data);
        handleSave({ target: { id: 'reject' }, currentTarget: { id: 'reject' } });
    }

    function validate() {
        const no_valid_fields = {};
        const req_fields = ['slug', 'picture'];
        const req_content_fields = ['title', 'seo_title', 'author_info'];

        req_fields.map((field) => {
            if (!data[field]) {
                no_valid_fields[field] = true;
            } else {
                no_valid_fields[field] = false;
            }
        });

        req_content_fields.map((field) => {
            LANGS.map((lang) => {
                if (data.content[lang].title || data.content[lang].text) {
                    if (!data.content[lang][field]) {
                        no_valid_fields[field] = true;
                    } else {
                        no_valid_fields[field] = false;
                    }
                }
            });
        });

        setValidation({
            ...validation,
            ...no_valid_fields,
        });
    }

    function preventReload(e) {
        e.preventDefault();
        e.returnValue = '';
    }

    function goToNewPage(newData) {
        history.push(`${PROJECT_PREFIX}/${project}/story/${type}/${newData.id}/edit`);
    }

    function goToPreviousPage(e) {
        const modifiedProject = type === 'static' ? '' : `/${project}`;

        history.push(`${PROJECT_PREFIX}/${project}/story/${type}/all`);
    }

    function initHistoryBlock(shouldBlockTransition) {
        if (transitionBlocker?.current) {
            transitionBlocker.current();
        }

        if (shouldBlockTransition) {
            window.addEventListener('beforeunload', preventReload);
            transitionBlocker.current = initTransitionBlocker();
        } else {
            window.removeEventListener('beforeunload', preventReload);
        }
    }

    function initTransitionBlocker() {
        return history.block(() => window.confirm('Вы уверены, что хотите закрыть страницу?\nВозможно, внесенные изменения не сохранятся.'));
    }

    return (
        <div>
            <Row className="show-grid">
                <Col md={11}>
                    <h3>
                        {singular[type]}
                        {isUgc ? ' (UGC)' : ''}
                        :
                        {' '}
                        {builtUrl ? (
                            <a
                                href={builtUrl}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {data.content[currentLang].title}
                            </a>
                        ) : (
                            data.content[currentLang].title
                        )}
                    </h3>
                </Col>
                <ConditionalRenderer currentProject={project} allowedProjects={gofProject}>
                    <Col md={1} className="justify-content-end">
                        <ToggleButtonGroup
                            value={currentLang}
                            name="lang"
                            key="inlineStyleBlock"
                            type="radio"
                            onChange={setLang}
                        >
                            <ToggleButton variant="outline-secondary" value="ru"> Ru </ToggleButton>
                            <ToggleButton variant="outline-secondary" value="en"> En </ToggleButton>
                        </ToggleButtonGroup>
                    </Col>
                </ConditionalRenderer>
            </Row>
            <Row className="show-grid">
                <Col md={8}>
                    <InputField
                        title="title"
                        label="Заголовок"
                        value={data.content[currentLang].title || ''}
                        onChange={handleTitleChange}
                        maxLength={76}
                        required
                        is_not_valid={validation.title || false}
                    />
                </Col>
                {/* <Col md={2}>
                    <InputField
                        name={'date_added'}
                        label={'Дата добавления'}
                        value={formatDateTime(data.date_added)}
                        onChange={handleChange}
                        readOnly={true}
                    />
                </Col> */}
                <ConditionalRenderer currentProject={project} disallowedProjects={viewsDisallowedProjects}>
                    <Col md={2}>
                        <InputField
                            name="views"
                            label="Просмотры"
                            value={data.views}
                            onChange={handleChange}
                            readOnly
                        />
                    </Col>
                </ConditionalRenderer>
                <ConditionalRenderer currentType={type} allowedTypes={ratingAllowedTypes}>
                    <Col md={2}>
                        <RatingSelectorField
                            name="rating"
                            label="Оценка"
                            value={data.rating}
                            onChange={handleChange}
                        />
                    </Col>
                </ConditionalRenderer>
            </Row>
            <Row className="show-grid">
                <Col md={8}>
                    {(type !== 'static' || permissions.endpoints.includes('admin')) && (
                        <InputField
                            name="slug"
                            label="Slug"
                            value={data.slug}
                            onChange={handleChange}
                            maxLength={150}
                            required
                            is_not_valid={validation.slug || false}
                        />
                    )}
                </Col>
                <Col md={2}>
                    <InputField
                        name="modified_at"
                        label="Дата изменения"
                        value={formatDateTime(data.modified_at)}
                        readOnly
                    />
                </Col>
                <Col md={2}>
                    <PermissionManager allowedEndpoints={['admin', 'senior-editor']}>
                        <ChangeHistory
                            id={storyId}
                            type={type}
                            project={project}
                            lastChange={data.change_history}
                        />
                    </PermissionManager>
                </Col>
            </Row>
            <Row>
                <Col md={8}>
                    <ConditionalRenderer currentProject={project} disallowedProjects={gofProject}>
                        <SeoSettings
                            handleChange={handleContentChange}
                            seo_title={data.content[currentLang].seo_title}
                            seo_descr={data.content[currentLang].seo_descr}
                            seo_text={data.content[currentLang].seo_text}
                            custom_canonical={data.content[currentLang].custom_canonical}
                            withSeoText={type === 'compilation'}
                        />
                    </ConditionalRenderer>
                </Col>
                <ConditionalRenderer currentType={type} allowedTypes={fileAllowedTypes}>
                    <Col md={4}>
                        <SearchSelectorField
                            name="tags"
                            label="Тэги"
                            value={data.tags}
                            uri={`${API_DOMAIN}tags/?type=mod`}
                            isMulti
                            onChange={handleChange}
                        />
                    </Col>
                </ConditionalRenderer>
            </Row>
            <Row>
                <Col md={8}>
                    <TextAreaEditorField
                        name="description"
                        label="Краткое описание"
                        value={data.content[currentLang].description}
                        onChange={handleContentChange}
                        maxLength={200}
                        noControls
                        rows={4}
                    />
                    <ConditionalRenderer
                        currentType={type}
                        allowedTypes={['video']}
                    >
                        <Row className="show-grid">
                            <Col md={12}>
                                <SelectorField
                                    name="type"
                                    label="Тип"
                                    selected={data.type}
                                    options={VIDEO_TYPES}
                                    onChange={handleChange}
                                />
                                <Row className="mt-2">
                                    <Col md={6}>
                                        <InputField
                                            name="youtube_video_url"
                                            label="Youtube"
                                            value={videoUrls?.youtube}
                                            onChange={handleChange}
                                        />
                                        <InputField
                                            name="ok_video_url"
                                            label="OK"
                                            value={videoUrls?.ok}
                                            onChange={handleChange}
                                        />
                                        <InputField
                                            name="mail_video_url"
                                            label="Mail"
                                            value={videoUrls?.mail}
                                            onChange={handleChange}
                                        />
                                        <InputField
                                            name="vk_video_url"
                                            label="VK"
                                            value={videoUrls?.vk}
                                            onChange={handleChange}
                                        />
                                    </Col>
                                    <Col md={5}>
                                        <VideoPlayer
                                            url={data.video_urls && data.video_urls[0]?.url}
                                            hosting={data.video_urls && data.video_urls[0]?.type}
                                            autoplay={false}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </ConditionalRenderer>
                </Col>
                <ConditionalRenderer currentProject={project} disallowedProjects={gofProject}>
                    <Col md={4}>
                        <SearchDragList
                            id="games"
                            title="Игры"
                            list={data.games}
                            onChange={handleChange}
                            searchUrl={`${API_DOMAIN}games/`}
                        />
                    </Col>
                </ConditionalRenderer>
            </Row>
            <ConditionalRenderer
                currentType={type}
                allowedTypes={['compilation']}
            >
                <Row>
                    <Col md={8} />
                    <Col md={4}>
                        <SearchDragList
                            id="related_stories"
                            title="Связанные подборки"
                            list={data.related_stories}
                            onChange={handleChange}
                            searchUrl={`${API_DOMAIN}v2/games.mail.ru/story/compilation/`}
                        />
                    </Col>
                </Row>
            </ConditionalRenderer>
            <ConditionalRenderer
                currentType={type}
                disallowedTypes={textDisallowedTypes}
            >
                <div style={{ height: '18px' }} />
                <Row>
                    { permissions.endpoints.includes('rawhtml') && (
                        <Col md={2}>
                            <Link className="mr-4" to={`${PROJECT_PREFIX}/rawhtml/all`} target="_blank">
                                Список сырых
                                HTML

                            </Link>
                        </Col>
                    )}
                    <Col md={2}>
                        <Link to={`${PROJECT_PREFIX}/table/all`} target="_blank">Список таблиц</Link>
                    </Col>
                    <Col md={2}>
                        <Link to={`${PROJECT_PREFIX}/quiz_trivia/all`} target="_blank">Список опросов</Link>
                    </Col>
                    <Col md={2}>
                        <Link to={`${PROJECT_PREFIX}/social_translations/all`} target="_blank">
                            Список соц.
                            переводов

                        </Link>
                    </Col>
                </Row>
            </ConditionalRenderer>
            <Row>
                <Col md={8}>
                    <ConditionalRenderer
                        currentType={type}
                        disallowedTypes={textDisallowedTypes}
                    >
                        <TextAreaEditorField
                            name="text"
                            label="Текст"
                            value={data.content[currentLang].text}
                            onChange={handleContentChange}
                            lessControls={project === 'gof.vkplay.ru'}
                            isStory
                            rows={25}
                        />
                        <SocialHint ref={socialHintRef} textareaSelector="textarea[name=text]" />
                    </ConditionalRenderer>
                </Col>
                <Col md={4}>
                    <Row>
                        <ConditionalRenderer currentType={type} allowedTypes={fileAllowedTypes}>
                            <Col md={12}>
                                <UploadFileInput
                                    id="url"
                                    inputId="file"
                                    label={`Загрузить файл (мод)${!data.id ? ' (чтобы разблокировать загрузку, сохраните материал)' : ''}`}
                                    value={data.media?.find(({ type }) => type === 'file')?.file}
                                    onFileSelect={handleFileUpload}
                                    disabled={!data.id}
                                    readOnly
                                />
                            </Col>
                        </ConditionalRenderer>
                        <Col md={12}>
                            <FastImageLoad
                                name="picture"
                                label="Обложка"
                                image={data.picture}
                                restrictions={`Разрешение не менее ${leadPicSize}. Формат: jpeg, png, webp.`}
                                minSize={leadPicSize}
                                onChange={handleChange}
                                required
                                is_not_valid={validation.picture || false}
                            />
                        </Col>
                        <ConditionalRenderer
                            currentProject={project}
                            disallowedProjects={gofProject}
                            currentType={type}
                            disallowedTypes={coversDisallowedTypes}
                        >
                            {!!data.picture && (
                                <Col md={12} className="image-cover__col">
                                    <ImageLeadField
                                        name="picture_square"
                                        label="Хайлайт (правый)"
                                        value={data.picture_square || data.picture}
                                        minSize={leadPicSquareSize}
                                        onChange={handleChange}
                                        onRemove={handleRemoveData}
                                        isDefault={!data.picture_square}
                                        className="image-cover__square"
                                        isWysiCover
                                        title={data.content[currentLang].title}
                                        author={data.content[currentLang].author_info && data.content[currentLang].author_info.nick}
                                    />
                                    <ImageLeadField
                                        name="picture_vertical"
                                        label="Мобильная Обложка"
                                        value={data.picture_vertical || data.picture}
                                        minSize={leadPicVertSize}
                                        onChange={handleChange}
                                        onRemove={handleRemoveData}
                                        isDefault={!data.picture_vertical}
                                        className="image-cover__vertical"
                                        isWysiCover
                                        title={data.content[currentLang].title}
                                        author={data.content[currentLang].author_info && data.content[currentLang].author_info.nick}
                                    />
                                </Col>
                            )}
                        </ConditionalRenderer>
                        <ConditionalRenderer
                            currentType={type}
                            disallowedTypes={galleryDisallowedTypes}
                        >
                            <Col md={12}>
                                <Gallery
                                    images={data.media}
                                    onImagesChange={handleImagesChange}
                                    onImageClick={handleImageClick}
                                    isLocked={!data.id}
                                />
                            </Col>
                        </ConditionalRenderer>
                    </Row>
                </Col>
            </Row>
            <ConditionalRenderer currentType={type} allowedTypes={disciplinesAllowedTypes}>
                <Row className="mb-3">
                    <Col md={3}>
                        <label className="form-label"><b>Дисциплины</b></label>
                        <SelectSearch
                            isMulti
                            id="disciplines"
                            name="channels"
                            staticData={disciplinesList}
                            default_values={filteredDisciplines}
                            excludes={[]}
                            searchValue={filteredDisciplines}
                            onChange={handleChange}
                        />
                    </Col>
                </Row>
            </ConditionalRenderer>
            {(type !== 'static' || permissions.endpoints.includes('admin')) && (
                <Row>
                    <Col md={3}>
                        <SearchSelectorField
                            name="author_info"
                            label="Автор редакции"
                            value={[data.content[currentLang].author_info]}
                            isAuthors
                            uri={`${API_DOMAIN}authors/`}
                            onChange={handleAuthorSearchChange}
                            isMulti={false}
                            required
                            is_not_valid={validation.author_info || false}
                        />
                        <Col md={8} className="p-0">
                            <p className="form-label"><b>Настройки материала</b></p>
                            <ConditionalRenderer
                                currentType={type}
                                disallowedTypes={checkboxesDisallowedTypes}
                            >
                                <CheckboxField
                                    name="in_progress"
                                    label="Материал дополняется"
                                    value={data.in_progress}
                                    onChange={handleChange}
                                />
                                <CheckboxField
                                    name="disable_comments"
                                    label="Запретить комментарии"
                                    value={data.disable_comments}
                                    onChange={handleChange}
                                />
                            </ConditionalRenderer>
                            <CheckboxField
                                name="is_published"
                                label="Опубликовано"
                                value={data.is_published}
                                onChange={handleChange}
                            />
                        </Col>
                    </Col>
                    <Col md={2}>
                        <DateTimeField
                            name="published_at"
                            label="Дата публикации (по Москве)"
                            value={data.content[currentLang].published_at || formatDateTimeForDRF(new Date(getMoscowTime()))}
                            onChange={handleDateChange}
                        />
                    </Col>
                    <ConditionalRenderer
                        currentType={type}
                        disallowedTypes={checkboxesDisallowedTypes}
                    >
                        <Col md={3}>
                            <ChannelsCheckboxes channels={data.channels} onChange={handleChange} storyType={type} />
                            <CheckboxField
                                name="by_direct_link"
                                label="Доступ только по ссылке"
                                value={data.by_direct_link}
                                onChange={handleChange}
                            />
                            <CheckboxField
                                name="is_list_hidden"
                                label="Не показывать на главной"
                                value={data.is_list_hidden}
                                onChange={handleChange}
                            />
                        </Col>

                    </ConditionalRenderer>
                </Row>
            )}
            {moderComments}
            <ControlPageBlock
                isClearableCache
                name={data.content[currentLang].title}
                storyType={type}
                onSave={handleSave}
                onApply={handleSave}
                onCancel={goToPreviousPage}
                onRemove={type !== 'static' ? handleRemoveStory : null}
                onReject={isUgc && !data.is_published && !data.is_draft && handleRejectUGC}
            />
        </div>
    );
}

export default withRouter(StoryEditScreenV2);
